/**
 * RACE SUMMARY finish gap breakdown: one bar per boat whose parts (start, maneuvers, marks, side,
 * speed) add up to its gap to the winner; losses right of zero, gains left. The day (`race` null) =
 * each boat's per-race average; `leg` set = what each boat lost to the leader over that leg.
 * Data: raceDrivers.ts.
 */
import { createMemo, For, Show } from "solid-js";
import SectionHead from "./SectionHead";
import { sourcesStore } from "../../../store/sourcesStore";
import { divergingScale, divergingSegments } from "./divergingParts";
import {
  DRIVER_BUCKETS,
  DRIVER_NOISE_M,
  type DriverRow,
  type DriversView,
} from "./raceDrivers";

interface Props {
  view: DriversView;
  /** Race number, or null for the day. */
  race: number | null;
  /** A leg of `race` (legDriversView): parts are metres lost to the leader over the leg. */
  leg?: number | null;
  /** Replaces the default heading when this panel is used outside a single day. */
  title?: string;
}

const metres = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(Math.round(v))} m`;

export default function RaceDriversPanel(props: Props) {
  const isDay = () => props.race == null;
  const isLeg = () => props.leg != null;
  /** Who the gaps are measured to: the winner, or on a leg the leader of the moment. */
  const ref = () => (isLeg() ? "leader" : "winner");

  /** Shared scale: largest summed gain to largest summed loss, rounded to a tick step. */
  const scale = createMemo(() =>
    divergingScale(
      props.view.rows.filter((r) => r.parts && !r.isWinner).map((r) => r.parts as Record<string, number>),
      DRIVER_BUCKETS,
      (span) => (span > 800 ? 200 : 100)
    )
  );

  const segments = (r: DriverRow) =>
    r.parts ? divergingSegments(r.parts as Record<string, number>, DRIVER_BUCKETS, scale()) : [];

  /** One short line per boat: its two biggest losses and its biggest gain, beyond the noise. */
  const note = (r: DriverRow): string => {
    if (!r.parts) {return "";}
    const parts = r.parts;
    const vals = DRIVER_BUCKETS.map((b) => ({ ...b, v: parts[b.key] }));
    const losses = vals.filter((s) => s.v >= DRIVER_NOISE_M).sort((a, b) => b.v - a.v).slice(0, 2);
    const gain = vals.filter((s) => s.v <= -DRIVER_NOISE_M).sort((a, b) => a.v - b.v)[0];
    const bits = losses.map((s) => {
      let extra = "";
      if (s.key === "mnvr" && r.maneuvers) {extra = ` (${Math.round(s.v / r.maneuvers)} m per tack or gybe)`;}
      if (s.key === "marks" && props.view.roundingsPerRace > 0) {extra = ` (${Math.round(s.v / props.view.roundingsPerRace)} m per rounding)`;}
      return `${s.label.toLowerCase()} ${Math.round(s.v)} m${extra}`;
    });
    if (gain) {bits.push(`gained ${Math.abs(Math.round(gain.v))} m on ${gain.label.toLowerCase()}`);}
    return bits.length ? `Lost most on ${bits.join(", ")}` : "Nothing beyond the noise";
  };

  /** Where the speed part was made: in phase (pointing closer to the course axis than the other tack)
   *  or out of phase. A big out-of-phase loss is the wrong tack for the shifts, not boat speed. */
  const phaseText = (r: DriverRow): string => {
    const ph = r.phase;
    if (!ph) {return "";}
    const pct = ph.pct != null ? `${Math.round(ph.pct)}% of straight sailing in phase` : "";
    if (r.isWinner) {return pct;}
    return [`Speed in phase ${metres(ph.inM)}`, `out of phase ${metres(ph.outM)}`, pct].filter(Boolean).join(" · ");
  };

  return (
    <section class="race-drivers race-drivers-card">
      <SectionHead
        title={
          props.title ??
          (isLeg() ? `Leg ${props.leg} gap breakdown` : isDay() ? "Finish gap breakdown, per race" : "Finish gap breakdown")
        }
      />
      <Show when={props.view.measuredEarly.length > 0}>
        <p class="race-drivers-note race-drivers-early">
          {props.view.measuredEarly
            .map((m) => `${isDay() ? `Race ${m.race}: ` : ""}compared at the end of leg ${m.leg} of ${m.legs}`)
            .join("; ")}
          {" "}(the data stopped before every boat finished, so every boat is measured over the same legs).
        </p>
      </Show>
      <div class="race-chart-legend race-drivers-legend">
        <For each={DRIVER_BUCKETS}>
          {(b) => (
            <span class="race-chart-legend-item">
              <span class="race-drivers-swatch" style={{ background: `var(${b.token})` }} />
              {b.label}
            </span>
          )}
        </For>
      </div>
      <div class="race-drivers-rows">
      {/* One zero line through every row, under the bars. */}
      <div class="race-drivers-row race-drivers-zero-overlay" aria-hidden="true">
        <div />
        <div class="race-drivers-zero-track">
          <div class="race-drivers-zero" style={{ left: `${scale().x(0)}%` }} />
        </div>
      </div>
      <div class="race-drivers-row race-drivers-sense-row">
        <div />
        <div class="race-drivers-sense">
          <Show when={scale().x(0) >= 18}>
            <span class="race-drivers-sense-gain" style={{ right: `${100 - scale().x(0)}%` }}>← Gained on the {ref()} (−)</span>
          </Show>
          <span class="race-drivers-sense-loss" style={{ left: `${scale().x(0)}%` }}>Lost to the {ref()} (+) →</span>
          <span class="race-drivers-sense-compact">− gained on the {ref()} · + lost to the {ref()}</span>
        </div>
      </div>
      <div class="race-drivers-row race-drivers-axis-row">
        <div />
        <div class="race-drivers-axis">
          <For each={scale().ticks}>
            {(t, i) => (
              <span
                class="race-drivers-num"
                classList={{ "race-drivers-axis-first": i() === 0, "race-drivers-axis-last": i() === scale().ticks.length - 1 }}
                style={{ left: `${scale().x(t)}%` }}
              >
                {t === 0 ? "0" : metres(t)}
              </span>
            )}
          </For>
        </div>
      </div>
      <For each={props.view.rows}>
        {(r) => (
          <div class="race-drivers-row race-drivers-boat">
            <div class="race-drivers-who">
              <div class="race-drivers-name">
                <span class="race-drivers-dot" style={{ background: sourcesStore.getSourceColor(r.boat) || "var(--color-text-secondary)" }} />
                {r.boat}
              </div>
              <div class="race-drivers-gap race-drivers-num">
                <Show when={r.parts} fallback="DNF">
                  <Show when={!r.isWinner} fallback={isLeg() ? "led the leg" : isDay() ? "won every race" : "winner"}>
                    {metres(r.gapM ?? 0)}{r.gapS != null ? ` · ${Math.round(r.gapS)} s` : ""}
                    <Show when={isLeg() && r.gapEndM != null}>{` · ${Math.round(r.gapEndM ?? 0)} m behind at the end`}</Show>
                  </Show>
                </Show>
                <Show when={isDay() && r.finished < r.raced}>{` · ${r.finished}/${r.raced} finished`}</Show>
              </div>
            </div>
            <div class="race-drivers-bar-wrap">
              <Show
                when={r.parts && !r.isWinner}
                fallback={
                  <span class="race-drivers-note">
                    {r.parts ? "Reference boat" : isLeg() ? "Not measured on this leg" : "Did not finish"}
                    <Show when={r.parts && phaseText(r)}>{` · ${phaseText(r)}`}</Show>
                  </span>
                }
              >
                <div class="race-drivers-bar">
                  <div class="race-drivers-zero race-drivers-zero-bar" style={{ left: `${scale().x(0)}%` }} />
                  <For each={segments(r)}>
                    {(s) => (
                      <div
                        class="race-drivers-seg"
                        classList={{ "race-drivers-seg-noise": Math.abs(s.v) < DRIVER_NOISE_M }}
                        style={{ left: `${s.left}%`, width: `calc(${s.width}% - 2px)`, background: `var(${s.token})` }}
                        title={`${r.boat} · ${s.label}: ${s.v > 0 ? "lost" : "gained"} ${Math.abs(Math.round(s.v))} m`}
                      >
                        {s.width > 9 ? metres(s.v) : ""}
                      </div>
                    )}
                  </For>
                </div>
                <span class="race-drivers-note">{note(r)}</span>
                <Show when={phaseText(r)}>
                  <span class="race-drivers-note race-drivers-phase race-drivers-num">{phaseText(r)}</span>
                </Show>
              </Show>
            </div>
          </div>
        )}
      </For>
      </div>
      <details class="race-drivers-method">
        <summary>How the numbers are made</summary>
        <Show when={isLeg()}>
          <p class="race-drivers-note">
            On a leg, each bar is what the boat lost (+) or gained (−) on the leader from the leader's previous rounding
            (the start, on leg 1) to the leader's rounding at the end of the leg, compared like for like (see below) and
            split into the same parts. A boat's legs add up to its finish gap. The boat that led the whole leg is the
            reference and is zero.
          </p>
        </Show>
        <Show when={!isLeg()}>
        <p class="race-drivers-note">
          Each bar is what the boat lost to the leader up to the winner's finish
          {isDay() ? ", averaged over the races it finished" : ""}, compared like for like: at every point of the course,
          the boat is measured against the leader when the leader sailed that same stretch, not at the same moment, and
          each second lost there counts at the leader's speed there. So a rounding is compared with the leader's own
          rounding of that mark, and a gap does not grow on a fast leg or shrink on a slow one just because of the
          speed. The total can differ from the DTL in the Results table, which is the distance on the water at the
          winner's finish. Every second goes into exactly one part, so the parts add up to the gap. + is metres lost,
          − is metres gained. The winner is the reference and is zero.
        </p>
        </Show>
        <dl class="race-drivers-defs">
          <dt>Start</dt>
          <dd>How far behind the winner the boat was 30 s after the gun: line position, timing and speed off the line together.</dd>
          <dt>Maneuvers</dt>
          <dd>
            Metres lost from 10 s before to 10 s after each tack and gybe, against the leader on the same stretch of
            course, net of the leader's own tacks and gybes: where the leader tacked and this boat sailed straight, its
            gain counts here too. Fewer or cheaper maneuvers than the leader read as a gain. A tack or gybe inside a mark
            rounding's window counts under Marks only, never in both, and "per tack or gybe" divides by the tacks and
            gybes away from the marks.
          </dd>
          <dt>Marks</dt>
          <dd>
            Metres lost or gained from 10 s before to 25 s after each rounding (and around each bear-away and round-up):
            the approach, the turn and the exit, against the leader's own rounding of the same mark.
          </dd>
          <dt>Side</dt>
          <dd>
            What being on the left or right of the course was worth, kept apart from how well the boat sailed. On each leg
            the course is split down the middle into a left and a right half (looking upwind). Taking every boat's
            straight-line sailing (not the leader), we see how fast boats in each half closed on the leader
            compared with the leg as a whole. Each boat is then credited that difference for every second it spent in a
            half. Example: on a leg where boats on the right closed on the leader 0.5 m/s faster than the leg average, a
            boat that sailed 2 minutes on the right gains 60 m on Side. That is the value of the water there (pressure,
            shifts, current), not of the boat: a boat at average pace in the half that paid still gains on Side, and how it
            sailed against that half goes into Speed. The Leg by leg table shows which half paid on each leg.
          </dd>
          <dt>Speed</dt>
          <dd>
            The rest of the straight-line sailing: the boat's own rate of gaining or losing on the leader, minus what its half
            was worth. Boat speed, height and VMG, mode and sail choice, and time on the wrong tack for the shifts.
          </dd>
          <dt>Speed in / out of phase</dt>
          <dd>
            The speed part split by which tack or gybe the boat was on. In phase is the one pointing closer to the course
            axis (VMC at least its VMG); out of phase is the other. Both are against the leader on the same stretch of
            course, so compare the teams. A team losing far more out of phase than the rest
            spent longer on the headed tack (the shifts, not boat speed); one losing in phase, where others gain, is short
            of speed or VMG. Seconds without a reading stay in Speed only, so the two may not add up to it exactly.
          </dd>
          <dt>What decided it</dt>
          <dd>Top of the recap: the part that varied most between the boats. Parts under {DRIVER_NOISE_M} m are faded.</dd>
        </dl>
      </details>
    </section>
  );
}
